/** Forespørsel-perspective of a work item: have we understood what the customer asks for? Nothing here is about price. */
export const KRAV_STATUS = { ny: "Ikke gjennomgått", klar: "Gjennomgått", avklares: "Trenger avklaring", opsjon: "Opsjon", ikke_tilbudt: "Utgår" };
export const KRAV_STATUS_OPTIONS = Object.entries(KRAV_STATUS);

export function kravState(item) {
  const c = item.calc || {};
  if (item.status === "ikke_tilbudt" || item.price_type === "utgar" || c.state === "utgar") return { key: "utgar", label: "Utgår", quiet: true };
  if (c.conflicts) return { key: "oppfolging", label: "Ulike opplysninger", warn: true };
  if (item.basis_changed && !item.basis_changed.confirmed) return { key: "oppfolging", label: item.basis_changed.kind === "fjernet" ? "Ikke i siste spesifikasjon" : item.basis_changed.kind === "ny" ? "Ny fra kunden" : "Kundens tekst endret", warn: true };
  if (c.open_clarifications || item.status === "avklares") return { key: "oppfolging", label: "Trenger avklaring", warn: true };
  if (item.status === "klar") return { key: "klar", label: null };
  if (item.status === "opsjon") return { key: "klar", label: "Opsjon", quiet: true };
  return { key: "gjenstar", label: null };
}

export function kravCounts(items) {
  const c = { alle: items.length, oppfolging: 0, klar: 0, utgar: 0, gjenstar: 0, avklaringer: 0 };
  items.forEach((i) => { c[kravState(i).key] += 1; c.avklaringer += i.calc?.open_clarifications || 0; });
  return c;
}

export const KRAV_SEL_KEY = (cid) => `sk.krav.sel.${cid}`;
const needsReview = (i) => ["gjenstar", "oppfolging"].includes(kravState(i).key);

/** «Fortsett gjennomgangen»: the post you left open if it still needs you, else the next one in the customer's order. */
export function nextReview(items, grouped, lastId) {
  const flat = grouped.flatMap((g) => g.rows);
  const last = lastId ? items.find((i) => i.id === lastId) : null;
  if (last && needsReview(last)) return { item: last, why: "der du slapp" };
  const from = last ? flat.findIndex((i) => i.id === last.id) + 1 : 0;
  const next = flat.slice(from).find(needsReview) || flat.find(needsReview);
  return next ? { item: next, why: next.calc?.open_clarifications || next.status === "avklares" ? "trenger avklaring" : "ikke gjennomgått" } : null;
}

/** Everything the Arbeidsomfang summary reads in one pass: progress, what is understood in our words, what still stands open. */
export function kravOverview(items, docs = []) {
  const c = kravCounts(items);
  const reviewed = c.klar + c.utgar;
  const own = items.filter((i) => (i.los_text || "").trim()).length;
  const sourced = items.filter((i) => i.source?.page || i.sheet_ref?.row || i.source?.type === "manuelt" || (i.source?.origin === "ki_tekst" && i.source?.doc_id)).length;
  const withDocs = items.filter((i) => attachmentCount(i, docs) > 0).length;
  const clarifications = items.flatMap((i) => [i, ...(i.children || [])].flatMap((x) => (x.clarifications || []).filter((q) => q.status !== "besvart" && q.status !== "lukket").map((q) => ({ item: x, q }))));
  const conflicts = items.filter((i) => i.calc?.conflicts).length;
  const open = [
    { key: "gjenstar", n: c.gjenstar, unit: ["post ikke gjennomgått", "poster ikke gjennomgått"] },
    { key: "oppfolging", n: c.oppfolging, unit: ["post trenger oppfølging", "poster trenger oppfølging"] },
    { key: "avklaringer", n: clarifications.length, unit: ["åpen avklaring", "åpne avklaringer"], filter: "oppfolging" },
    { key: "konflikter", n: conflicts, unit: ["post med ulike opplysninger", "poster med ulike opplysninger"], filter: "oppfolging" },
    { key: "uten_forstaelse", n: items.length - own, unit: ["post uten «vår forståelse»", "poster uten «vår forståelse»"], quiet: true, filter: "gjenstar" },
  ].filter((x) => x.n > 0);
  return { counts: c, reviewed, pct: items.length ? Math.round((reviewed / items.length) * 100) : 0, own, sourced, withDocs, clarifications, conflicts, open, done: items.length > 0 && !c.gjenstar && !c.oppfolging };
}

export const KRAV_FILTERS = [["alle", "Alle"], ["oppfolging", "Trenger oppfølging"], ["gjenstar", "Ikke gjennomgått"], ["klar", "Gjennomgått"], ["utgar", "Utgår"]];
/** Older UI hints («avklaring») and calc states map onto the Arbeidsomfang filters. */
export const normalizeKravFilter = (f) => (!f || f === "alle" ? null : f === "avklaring" || f === "ma_vurderes" ? "oppfolging" : f === "venter_pris" ? "gjenstar" : f);

export function matchKrav(item, q) {
  if (!q) return true;
  const s = q.toLowerCase();
  return [item.ref, item.wo_no, item.title, item.customer_text, item.los_text, item.group].some((v) => (v || "").toLowerCase().includes(s));
}

/** Sections open by default where there is still work; a section that is fully reviewed starts closed. */
export const sectionOpenByDefault = (rows) => rows.some((i) => ["oppfolging", "gjenstar"].includes(kravState(i).key));

/* ---- What kind of object is this row in the customer's export? ---- */
const INFO_RE = /^\s*(forespørsel|generelt|informasjon|general|introduksjon|innledning)\b/i;
const TERMS_RE = /^\s*(vilkår|betingelser|terms|conditions)\b/i;
const BUDGET_RE = /^\s*(budsjett|budget)\b/i;

export const ROLE_LABEL = { arbeid: "Arbeidspost", informasjon: "Informasjon", budsjett: "Budsjettlinje", vilkar: "Vilkår" };
export const ROLE_OPTIONS = Object.entries(ROLE_LABEL);

/** The transparent reading of the row – title, ref, group – before anyone has classified it. */
export function heuristicKind(item) {
  const title = item.title || "";
  if (BUDGET_RE.test(title) || BUDGET_RE.test(item.group || "") || (item.sfi || "").replace(/\D/g, "") === "00") return "budsjett";
  if (TERMS_RE.test(title)) return "vilkar";
  if (item.calc?.info_like || INFO_RE.test(title) || (!item.wo_no && /^0{2,3}\.\d+$/.test(item.ref || "") && /diverse/i.test(item.group || ""))) return "informasjon";
  return "arbeid";
}

/**
 * The customer's table of contents mixes rows: the request itself (introduction, terms – «000.1 Forespørsel»), budget/sum lines
 * («00.00.1 Budsjett: Verksted/Yard») and the actual jobs on the vessel. Only the jobs are scope of work. An explicit `role` set
 * by Egil wins; until then the heuristic reads the row – and the UI may *suggest* a role, never set one.
 */
export function itemKind(item) {
  if (item.role && ROLE_LABEL[item.role]) return item.role;
  return heuristicKind(item);
}
/** A row nobody has classified that does not read as a job: the role we would suggest, or null. */
export function roleSuggestion(item) {
  if (item.role || item.parent_id) return null;
  const k = heuristicKind(item);
  return k === "arbeid" ? null : k;
}
export const KIND_LABEL = { informasjon: "Fra forespørselen", budsjett: "Budsjett", vilkar: "Vilkår", arbeid: "Arbeidspost" };
export const workItems = (items) => items.filter((i) => itemKind(i) === "arbeid");
export const metaItems = (items) => items.filter((i) => itemKind(i) !== "arbeid");

/** SFI main group («2» for 200.1) – the digit the customer's own table of contents uses. */
export const sfiMain = (item) => { const s = String(item.sfi || item.ref || "").trim(); const m = s.match(/^0*(\d)/); return m ? (s.startsWith("00") ? "0" : m[1]) : null; };

/** Group the (already filtered) rows into the customer's sections, in the order they appear in the document. */
export function groupItems(rows) {
  const order = [];
  const by = {};
  rows.forEach((i) => {
    const g = i.group || "";
    if (!by[g]) { by[g] = { key: g, label: g || "Uten gruppe", sfi: sfiMain(i), rows: [] }; order.push(g); }
    by[g].rows.push(i);
  });
  return order.map((g) => by[g]);
}

/** Fjord1's export wraps fields in tags («[Details] …», «[JobDescription] Jobb tittel: … Jobb nr: … Annet :»). Readers want the sentence, not the markup. */
export function cleanCustomerText(s) {
  let t = (s || "").replace(/\[(Details|JobDescription|Description|Remarks?|Notes?|Comments?|Attachments?|Yard ?jobs?)\]\s*/gi, "\n");
  t = t.replace(/Jobb tittel:\s*[^\n]*?\s+Jobb nr:\s*\S+\s*(?:Annet\s*:\s*)?/gi, "");
  t = t.replace(/\b(?:Job title|Jobb tittel|Jobb nr|Job no)\s*:\s*/gi, "");
  return t.replace(/\s*\n\s*/g, " ").replace(/\s+/g, " ").replace(/^[\s:.,;–-]+/, "").trim();
}

/** The customer's text as readable paragraphs: tagged fields become labelled blocks, the export's job-header boilerplate is dropped. */
export function customerParagraphs(s) {
  const LABEL = { details: null, description: null, jobdescription: "Jobbeskrivelse", remark: "Merknad", remarks: "Merknad", note: "Merknad", notes: "Merknad", comment: "Kommentar", comments: "Kommentar", attachments: "Vedlegg", attachment: "Vedlegg" };
  const parts = (s || "").split(/\[([A-Za-z ]+)\]\s*/);
  const out = [];
  const push = (label, text) => { const t = text.replace(/Jobb tittel:\s*[^\n]*?\s+Jobb nr:\s*\S+\s*(?:Annet\s*:\s*)?/gi, "").replace(/\b(?:Job title|Jobb tittel|Jobb nr|Job no)\s*:\s*/gi, "").trim().replace(/^[\s:.,;–-]+/, ""); if (t) out.push({ label, text: t }); };
  if (parts.length === 1) { push(null, parts[0]); return out; }
  if (parts[0].trim()) push(null, parts[0]);
  for (let i = 1; i < parts.length; i += 2) {
    const key = parts[i].replace(/\s+/g, "").toLowerCase();
    push(key in LABEL ? LABEL[key] : parts[i], parts[i + 1] || "");
  }
  return out;
}

/** One or two short sentences for a list row – LOS' structured understanding when it exists, otherwise the start of the customer's text. */
export function rowSummary(item, max = 190) {
  const own = (item.los_text || "").replace(/\s+/g, " ").trim();
  const src = own || cleanCustomerText(item.customer_text);
  if (!src) return { text: "", own: false };
  if (src.length <= max) return { text: src, own: Boolean(own) };
  const cut = src.slice(0, max);
  const end = Math.max(cut.lastIndexOf(". "), cut.lastIndexOf("! "), cut.lastIndexOf("? "));
  return { text: end > max * 0.45 ? cut.slice(0, end + 1) : `${cut.replace(/\s+\S*$/, "")} …`, own: Boolean(own) };
}

/** Attachments the customer mentions for this row, plus documents actually linked to it. */
export function attachmentCount(item, docs = []) {
  const linked = docs.filter((d) => (item.doc_ids || []).includes(d.id) || (d.linked_item_ids || []).includes(item.id)).length;
  return Math.max(linked, (item.attachment_names || []).length);
}

/** Bullet or numbered lines in «vår forståelse» become a readable list of operations – only when there really are several. */
export function workSteps(item) {
  const lines = (item.los_text || "").split(/\n+/).map((l) => l.replace(/^\s*(?:[-–•*]|\d+[.)])\s*/, "").trim()).filter(Boolean);
  const bullets = (item.los_text || "").split(/\n+/).filter((l) => /^\s*(?:[-–•*]|\d+[.)])\s+/.test(l));
  return bullets.length >= 2 ? lines : [];
}
